import { useEffect, useState } from 'react'
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom'
import { Building2, ChevronLeft } from 'lucide-react'
import { useAuth } from '@/auth/AuthContext'
import { admits, atCompanyLevel, setsUpIntegrations, venuesUpTarget } from '@/auth/access'
import { PIPELINE_COUPLE_LISTS, type PipelineCoupleList } from '@lib/constants/pipeline'
import type { VenuePipelineDto } from '@lib/dto/pipeline.dto'
import type { FunnelTarget } from '@lib/enums/funnel-stage.enum'
import { CoupleSheet, type PipelineOpened } from '@/components/pipeline/CoupleSheet'
import { LookAtPanel } from '@/components/pipeline/LookAtPanel'
import { LostPanel } from '@/components/pipeline/LostPanel'
import { PacePanel } from '@/components/pipeline/PacePanel'
import { PipelineFunnel } from '@/components/pipeline/PipelineFunnel'
import { PipelineSummary } from '@/components/pipeline/PipelineSummary'
import { PipelineTiles } from '@/components/pipeline/PipelineTiles'
import { companyConnections, connectableText, PIPELINE_VIEW_LABELS } from '@/components/pipeline/pipelineText'
import { SourceTable } from '@/components/pipeline/SourceNumbers'
import { TrackingPanel } from '@/components/pipeline/TrackingPanel'
import { usePipelineQuery } from '@/components/pipeline/usePipelineQuery'
import { InfoNote, PeriodBar, ViewToggle } from '@/components/stats/StatsPieces'
import { SplitFilter } from '@/components/pipeline/SplitFilter'
import { SpanControls } from '@/components/pipeline/SpanControls'
import { PIPELINE_SPAN_PERIODS } from '@lib/enums/pipeline-span.enum'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { Button } from '@/components/ui/button'
import { Callout } from '@/components/ui/callout'
import { DetailSkeleton, EmptyState, ErrorState } from '@/components/ui/states'
import { isApiError, requestErrorMessage } from '@/helpers/apiClient'
import { useVenuePipeline } from '@/hooks/usePipeline'
import { formatAdPeriod, formatMicros } from '@/lib/adFormat'
import { formatDate } from '@lib/helpers/format'
import { venueStoryInput } from '@lib/helpers/pipeline-story'
import { useOrganization } from '@/hooks/useOrganization'

/** A list a link names (`?list=toured_deciding`), when it is one. */
function isCoupleList(value: string | null): value is PipelineCoupleList {
  return PIPELINE_COUPLE_LISTS.some((list) => list === value)
}

/** What the notes at the foot of the page say, each only when it applies. */
function Notes({ data }: { data: VenuePipelineDto }) {
  const cohort = data.view === 'cohort'
  return (
    <div className="space-y-1.5">
      {cohort && data.pace === null && data.stages.some((s) => s.stillMoving) ? (
        <InfoNote>Couples who inquired in the last eight weeks are still deciding, so these counts will rise.</InfoNote>
      ) : null}
      {cohort ? null : <InfoNote>Shares and what’s usual show for couples who inquired, where each couple is followed from their inquiry.</InfoNote>}
      {data.cost === null ? null : (
        <InfoNote>Inquiries, tours and bookings are priced from what marketing cost, ads and listing fees, once there are at least three.</InfoNote>
      )}
      {data.partlyRecordedBefore === null ? null : (
        <InfoNote>
          Periods before {formatDate(data.partlyRecordedBefore)} are partly recorded: HoneyBook dated each inquiry and each lead’s current stage, but not the
          steps before.
        </InfoNote>
      )}
      {data.switchedOn === null ? null : (
        <InfoNote>
          This venue switched from HoneyBook on {formatDate(data.switchedOn)}: periods before then are counted from what HoneyBook recorded, later ones
          from the venue’s own events.
        </InfoNote>
      )}
    </div>
  )
}

/**
 * Ad spend with no inquiry to show for it: either the ads brought no one, or
 * nothing here records the inquiries they brought. Said loudly, since a quiet
 * "no cost" read as nothing to worry about.
 */
function SpendWithoutInquiries({ data, connects }: { data: VenuePipelineDto; connects: string | null }) {
  const inquiries = data.stages.find((s) => s.target === 'inquiry')?.value ?? null
  if (data.spend === null || data.spend.spendMicros === 0 || inquiries !== 0) return null
  return (
    <Callout tone="attention" title={`Ads cost ${formatMicros(data.spend.spendMicros, data.spend.currencyCode)} in these weeks, and no inquiries were recorded.`}>
      {spendAdvice(data, connects)}
    </Callout>
  )
}

/** What to check when ads brought no inquiries: where they should arrive, or what would count them, for what the company has. */
function spendAdvice(data: VenuePipelineDto, connects: string | null): string {
  const reads = [data.updatedAt === null ? null : 'GoHighLevel', data.honeybookUpdatedAt === null ? null : 'HoneyBook'].filter((n) => n !== null)
  if (reads.length > 0) return `Check that the ads are running and that their forms still reach ${reads.join(' or ')}.`
  const counted = 'Inquiries here are counted from the events made in FLOH Venues.'
  return connects === null ? `${counted} Check that the ads are running and that their inquiries become events.` : `${counted} Connecting ${connects} counts every inquiry it holds.`
}

/**
 * A venue's pipeline (docs/pipeline-view.spec.md), laid out for the day-to-day
 * marketer: the short version first, then the numbers they report, where
 * couples drop off against what's usual here, how bookings are pacing, where
 * couples came from and why they were lost, who to look at now, and what the
 * numbers can't see. Any count opens the couples behind it, for those who also
 * see the events.
 */
export function VenuePipelinePage() {
  const { user } = useAuth()
  const up = venuesUpTarget(user)
  const { venueId = '' } = useParams()
  const location = useLocation()
  const features = useOrganization().data?.features ?? []
  const [query, setQuery] = usePipelineQuery()
  const [opened, setOpened] = useState<PipelineOpened | null>(null)
  const [params, setParams] = useSearchParams()
  const pipelineQuery = useVenuePipeline(venueId, query)
  const data = pipelineQuery.data
  const loaded = data !== undefined
  const canList = data?.canListCouples ?? false

  // The period email links straight to a list (?list=…): it opens once the
  // page has drawn, for those who see the couples, and leaves the address so
  // closing it doesn't open it again.
  const linked = params.get('list')
  useEffect(() => {
    if (!loaded || linked === null) return
    if (canList && isCoupleList(linked)) setOpened({ kind: 'list', list: linked })
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        next.delete('list')
        return next
      },
      { replace: true },
    )
  }, [loaded, linked, canList, setParams])

  // The company's table links to a venue's tracking (#tracking): there once the page has drawn it.
  useEffect(() => {
    const id = decodeURIComponent(location.hash.replace(/^#/, ''))
    if (loaded && id !== '') document.getElementById(id)?.scrollIntoView({ block: 'start' })
  }, [location.hash, loaded])

  if (pipelineQuery.isPending) {
    return (
      <PageContainer width="wide">
        <DetailSkeleton />
      </PageContainer>
    )
  }
  // The venue is gone, or it is not one this person can open. The API already
  // said so; retrying asks the same question. The way on is back to the list
  // they work from — Venues for someone across the company, and the place
  // `venuesUpTarget` names for everyone else, who have no such list.
  if (isApiError(pipelineQuery.error) && pipelineQuery.error.status === 404) {
    return (
      <PageContainer width="wide">
        <EmptyState
          icon={Building2}
          title="That venue could not be found"
          description="It may have been removed, or it is not one you can open."
          action={
            <Button asChild>
              <Link to={up.to}>Back to {up.label.toLowerCase()}</Link>
            </Button>
          }
        />
      </PageContainer>
    )
  }
  if (pipelineQuery.isError || data === undefined) {
    return (
      <PageContainer width="wide">
        <ErrorState message={requestErrorMessage(pipelineQuery.error)} onRetry={() => void pipelineQuery.refetch()} />
      </PageContainer>
    )
  }

  const window = data.window
  // The newest period shown, which names these weeks to the couple list: the one holding their last day.
  const newest = window === null ? undefined : data.periods.find((p) => p.start <= window.end)?.start
  const onOpen = data.canListCouples ? (target: FunnelTarget) => setOpened({ kind: 'count', target }) : undefined
  const onList = data.canListCouples ? (list: PipelineCoupleList) => setOpened({ kind: 'list', list }) : undefined
  const companyHas = companyConnections(features)
  return (
    <PageContainer width="wide" className="space-y-6">
      {/* The company's Pipeline table leads here: the way back to it, for whoever can open it. */}
      {atCompanyLevel(user) && admits(user, { staff: 'leadStats:view', companyWide: true }) ? (
        <Link to={`/pipeline${location.search}`} className="inline-flex items-center gap-1 text-sm font-medium text-ink-muted transition-colors hover:text-ink">
          <ChevronLeft className="h-4 w-4" /> Pipeline
        </Link>
      ) : null}
      <div className="space-y-1">
        <PageHeader title="Pipeline" meta={data.venueName} />
        {data.switchedOn === null ? null : <p className="m-0 text-small text-ink-muted">Switched from HoneyBook on {formatDate(data.switchedOn)}</p>}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <ViewToggle view={data.view} labels={PIPELINE_VIEW_LABELS} onView={(view) => setQuery({ ...query, view })} />
        <SplitFilter splits={data.splits} split={data.split} onSplit={(split) => setQuery({ ...query, split })} />
        <SpanControls
          span={query.span}
          compare={query.compare}
          onSpan={(span) => setQuery({ ...query, span })}
          onCompare={(compare) => setQuery({ ...query, compare })}
        />
      </div>

      {window === null ? (
        <p className="m-0 text-sm text-ink-muted">No periods yet.</p>
      ) : (
        <>
          <PeriodBar
            window={window}
            periods={data.periods}
            spend={null}
            step={PIPELINE_SPAN_PERIODS[query.span]}
            onPeriod={(period) => setQuery({ ...query, period })}
          />
          <SpendWithoutInquiries data={data} connects={connectableText(companyHas)} />
          <PipelineSummary input={venueStoryInput(data)} />
          <PipelineTiles data={data} />
          <PipelineFunnel data={data} onOpen={onOpen} onList={onList} />
          {data.view === 'cohort' && data.pace !== null ? <PacePanel pace={data.pace} /> : null}
          {/* Where couples came from is counted on All couples only, as spend is. */}
          {data.split === 'all' ? (
            <section id="sources" aria-label="Where couples came from">
              <SourceTable
                rows={data.sources}
                showsMoney={data.sourceSummary !== null}
                showsCostPerInquiry
                marksUnbooked
                actions={
                  data.sourceSummary !== null && data.sources.some((r) => r.channel === 'google_ads') ? (
                    <Link to={`/venues/${venueId}/ads`} className="text-sm font-medium text-ink-muted transition-colors hover:text-ink">
                      Open in Ads
                    </Link>
                  ) : undefined
                }
              />
            </section>
          ) : null}
          <div className="flex flex-wrap items-stretch gap-6">
            <LostPanel reasons={data.lostReasons} view={data.view} />
            {data.lookAt === null ? null : <LookAtPanel lookAt={data.lookAt} onList={onList} />}
            <TrackingPanel
              data={data}
              canSetUp={setsUpIntegrations(user)}
              companyHas={companyHas}
              onUnlinked={() => setQuery({ ...query, split: 'unlinked' })}
            />
          </div>
          <Notes data={data} />
          <CoupleSheet
            venueId={venueId}
            timeZone={data.timeZone}
            query={{ split: data.split, view: data.view, span: data.span, ...(newest === undefined ? {} : { period: newest }) }}
            opened={opened}
            periodLabel={formatAdPeriod(window.start, window.end)}
            lookAt={data.lookAt}
            onClose={() => setOpened(null)}
          />
        </>
      )}

      <p className="m-0 text-small text-ink-muted">
        Two-week periods from Sunday, in the venue’s time zone.{' '}
        {data.view === 'cohort' ? 'Couples count in the weeks they first inquired.' : 'Each count is what happened during the weeks shown.'}
      </p>
    </PageContainer>
  )
}
